// public/v3/src/components/time-entry-edit-modal.jsx
// Inline modal for editing or deleting a single activityLog entry on a patient.
//
// The patient's activityLog is stored on the patient blob (data.activityLog),
// so saving/deleting an entry requires PUT-ing the FULL patient data back
// with the entry replaced or removed. Mirrors the persistence pattern used
// by addTime() / undoEntry() in time-logs.jsx.
//
// onSaved is called after a successful PUT with the updated patient data
// (the full data blob) so the caller can refresh local state / draft cache.

import React, { useState, useEffect } from 'react';
import { ccmApi } from '../lib/api';
import { useUnsavedChangesGuard } from '../lib/hooks/use-unsaved-changes-guard';

function staffNameForUser(user) {
  return String(user?.displayName || user?.name || user?.email || '').trim();
}

function normalizedEntryType(entry, label) {
  const existing = String(entry?.type || '').trim();
  if (existing && existing.toLowerCase() !== 'other') return existing;
  const labeled = String(entry?.label || label || '').trim();
  return labeled || 'Other';
}

export function TimeEntryEditModal({ patient, entry, currentUser, onClose, onSaved }) {
  const initialMins = Number(entry?.duration) || Number(entry?.mins) || 0;
  const initialDate = entry?.date || '';
  const initialLabel = entry?.notes || entry?.desc || entry?.label || '';

  const [date, setDate] = useState(initialDate);
  const [duration, setDuration] = useState(initialMins);
  const [label, setLabel] = useState(initialLabel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const { attemptDismiss, showDiscardPrompt, confirmDiscard, keepEditing } = useUnsavedChangesGuard({
    values: { date, duration, label },
    onClose,
    busy,
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attemptDismiss]);

  if (!patient || !entry) return null;

  // Build the next data blob with the entry replaced (or appended if _isNew).
  // For existing entries: keeps id/pid/ts/presetId untouched; only date/duration/desc/label change.
  // For new entries: appends to the log.
  function buildUpdatedLog() {
    const log = Array.isArray(patient._raw?.activityLog) ? patient._raw.activityLog : [];
    const nextLabel = String(label || '').trim();
    const staff = entry.staff || staffNameForUser(currentUser);
    const updated = {
      ...entry,
      date,
      duration: Number(duration) || 0,
      mins: Number(duration) || 0,
      desc: nextLabel,
      label: nextLabel,
      notes: nextLabel,
      type: normalizedEntryType(entry, nextLabel),
    };
    if (entry._isNew && staff) updated.staff = staff;
    if (entry._isNew && currentUser?.id) updated.staffUserId = currentUser.id;
    delete updated._isNew; // strip UI flag before persisting
    if (entry._isNew) {
      return [...log, updated];
    }
    return log.map((e) => (e && e.id === entry.id ? updated : e));
  }

  function buildDeletedLog() {
    const log = Array.isArray(patient._raw?.activityLog) ? patient._raw.activityLog : [];
    return log.filter((e) => e && e.id !== entry.id);
  }

  function validate() {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Date is required (YYYY-MM-DD).';
    const n = Number(duration);
    if (!Number.isFinite(n) || n < 1 || n > 480) return 'Duration must be 1–480 minutes.';
    if (!String(label || '').trim()) return 'Description is required.';
    return null;
  }

  // Preserve _updatedAt on the body so the server's 409 optimistic-lock fires
  // on stale writes (server/routes/patients.js:117-194). Capture the response's
  // new updatedAt into nextData so the parent's local cache stays in sync.
  async function persist(nextLog) {
    if (!patient._raw) throw new Error('patient data not loaded');
    const nextData = { ...patient._raw, activityLog: nextLog };
    const resp = await ccmApi.updatePatient(patient.id, nextData);
    const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
    return fresh ? { ...nextData, _updatedAt: fresh } : nextData;
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setBusy(true);
    try {
      const nextData = await persist(buildUpdatedLog());
      onSaved?.(nextData);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
        // eslint-disable-next-line no-console
        console.error('TimeEntryEditModal save 409:', e);
      } else {
        const msg = e?.message || 'Save failed';
        setError(msg);
        // Surface in console too for support.
        // eslint-disable-next-line no-console
        console.error('TimeEntryEditModal save failed:', e);
        alert(`Could not save the entry: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    setError(null);
    setBusy(true);
    try {
      const nextData = await persist(buildDeletedLog());
      onSaved?.(nextData);
    } catch (e) {
      if (e && e.status === 409) {
        setError('Patient was updated elsewhere. Refresh and try again.');
        // eslint-disable-next-line no-console
        console.error('TimeEntryEditModal delete 409:', e);
      } else {
        const msg = e?.message || 'Delete failed';
        setError(msg);
        // eslint-disable-next-line no-console
        console.error('TimeEntryEditModal delete failed:', e);
        alert(`Could not delete the entry: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={attemptDismiss}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={entry._isNew ? 'Add time entry' : 'Edit time entry'}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}>
        <div className="modal-head">
          <div>
            <h3>{entry._isNew ? 'Add time entry' : 'Edit time entry'}</h3>
            <div className="sub">
              {patient?.name || patient?._raw?.fname || patient?.id}
              {entry.kind ? <> · <span style={{ color: 'var(--ink-3)' }}>{entry.kind}</span></> : null}
            </div>
          </div>
          <button className="btn ghost icon" onClick={attemptDismiss} aria-label="Close">✕</button>
        </div>
        {showDiscardPrompt ? (
          <>
            <div className="modal-body">
              <h3 style={{ marginTop: 0 }}>Discard changes?</h3>
              <p>You've made changes but haven't saved yet.</p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={keepEditing}>Keep editing</button>
              <button className="btn warn" onClick={confirmDiscard}>Discard</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <div className="field">
                <div className="field-label">Date</div>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={busy}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r)', fontSize: 14, background: 'var(--surface)', color: 'var(--ink-1)' }}
                />
              </div>
              <div className="field">
                <div className="field-label">Duration (minutes)</div>
                <input
                  type="number"
                  min={1}
                  max={480}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  disabled={busy}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r)', fontSize: 14, background: 'var(--surface)', color: 'var(--ink-1)' }}
                />
              </div>
              <div className="field">
                <div className="field-label">Description</div>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={busy}
                  maxLength={120}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r)', fontSize: 14, background: 'var(--surface)', color: 'var(--ink-1)' }}
                />
              </div>
              {error && (
                <div style={{ color: 'var(--bad, #d33)', fontSize: 13, marginTop: 8 }}>{error}</div>
              )}
            </div>
            <div className="modal-foot">
              {!entry._isNew ? (
                <button
                  className="btn ghost sm"
                  onClick={handleDelete}
                  disabled={busy}
                  style={{ color: 'var(--bad, #d33)' }}
                  title="Delete this entry">
                  Delete
                </button>
              ) : <span/>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={attemptDismiss} disabled={busy}>Cancel</button>
                <button className="btn primary" onClick={handleSave} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
